import { OlympsConfigOlymp } from '@yandex-int/k-common';
import { CertificateBanner, CERTIFICATE_LAYOUT, useIntl } from 'platform-components';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import ym from 'react-yandex-metrika';

import { downloadArchive } from 'utils/download-file';

import { getCanLoadStudentsCertificates, getLessonsCertificatesByStudentId } from '../selectors';
import { GenerateCertsZipButton } from '../__generate-certs-zip-button/results__generate-certs-zip-button';

interface Props {
    olympConfig?: OlympsConfigOlymp;
}

interface Params {
    courseId: string;
}

// eslint-disable-next-line complexity
export const StudentsCertificatesBanner = ({ olympConfig }: Props) => {
    const { formatMessage } = useIntl();
    const i18n = useCallback((id: string) => formatMessage({ id: `specCourse.lessonView.${id}` }), [formatMessage]);

    const params = useParams<Params>();
    const courseId = Number(params.courseId);

    const certificates = useSelector((storeState) => getLessonsCertificatesByStudentId(storeState, { courseId }));
    const canDownloadCertificates = useSelector((storeState) =>
        getCanLoadStudentsCertificates(storeState, { courseId })
    );

    const [archiveIsLoading, setArchiveIsLoading] = useState(false);

    let hasBeGeneratedPdfs = false,
        hasFeGeneratedPdfs = false;

    for (const { template, certificate } of Object.values(certificates)) {
        if (template) {
            hasFeGeneratedPdfs = true;
        } else if (certificate) {
            hasBeGeneratedPdfs = true;
        }
    }

    useEffect(() => {
        if (hasFeGeneratedPdfs && hasBeGeneratedPdfs) {
            window.Ya.Rum.logError({
                message: 'Archive should not have both FE- and BE-generated pdfs',
                additional: {
                    courseId,
                    slug: olympConfig?.slug,
                },
            });
        }
    }, [hasFeGeneratedPdfs, hasBeGeneratedPdfs, courseId, olympConfig?.slug]);

    const handleClick = useCallback(() => {
        setArchiveIsLoading(true);

        const fileName = i18n('studentsCertificates.archive.fileName');
        downloadArchive(
            Object.values(certificates).map((x) => x.certificate!),
            fileName
        ).finally(() => {
            setArchiveIsLoading(false);
        });

        ym('reachGoal', 'teacher_olymp_download_students_diploma');
    }, [certificates, i18n]);

    const bannerParams = olympConfig?.teacher?.results?.banners?.studentsCertificatesBanner;

    const buttonText = bannerParams?.buttonText || i18n('studentsCertificatesBanner.button');

    return (
        <CertificateBanner
            buttonProps={{ progress: archiveIsLoading }}
            buttonText={buttonText}
            description={
                canDownloadCertificates
                    ? bannerParams?.descriptionWithDownloadAllowed || i18n('studentsCertificatesBanner.description')
                    : bannerParams?.descriptionWithScoringInProgress ||
                      i18n('studentsCertificatesBanner.certificateGenerationDescription')
            }
            headerText={bannerParams?.headerText || i18n('studentsCertificatesBanner.header')}
            imageSrc={bannerParams?.imgSrc || i18n('studentsCertificatesBanner.imgSrc')}
            layout={CERTIFICATE_LAYOUT.WIDE}
            onClick={handleClick}
            showButton={
                canDownloadCertificates &&
                (!hasFeGeneratedPdfs || (
                    <GenerateCertsZipButton certificates={certificates} courseId={courseId}>
                        {buttonText}
                    </GenerateCertsZipButton>
                ))
            }
            theme="purple"
        />
    );
};
